import React, { useEffect, useState } from "react";
import { bankService, BankResponse } from "../service/bankService";
import { useToast } from "@customHooks/useToast";
import { Loader2, CreditCard } from "lucide-react";
import { Badge } from "@components/shadcn-ui/badge";

interface BankDisplayProps {
  applicationId: string;
}

export const BankDisplay: React.FC<BankDisplayProps> = ({ applicationId }) => {
  const [banks, setBanks] = useState<BankResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    if (applicationId) {
      fetchBanks();
    } else {
      setIsLoading(false);
    }
  }, [applicationId]);

  const fetchBanks = async () => {
    setIsLoading(true);
    try {
      const response = await bankService.getBanksByApplicationId(applicationId);
      setBanks(response.bankAccounts || []);
    } catch (error) {
      console.error("Error fetching banks:", error);
      toast({
        variant: "destructive",
        description: "Failed to load bank accounts",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getAccountTypeColor = (type: string) => {
    switch (type) {
      case "SAVINGS":
        return "bg-green-100 text-green-800";
      case "CURRENT":
        return "bg-blue-100 text-blue-800";
      case "SALARY":
        return "bg-purple-100 text-purple-800";
      case "FIXED_DEPOSIT":
        return "bg-amber-100 text-amber-800";
      case "RECURRING_DEPOSIT":
        return "bg-indigo-100 text-indigo-800";
      case "NRE":
        return "bg-pink-100 text-pink-800";
      case "NRO":
        return "bg-teal-100 text-teal-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Mask account number to show only last 4 digits
  const maskAccountNumber = (accountNumber: string) => {
    if (!accountNumber) return "";
    const lastFour = accountNumber.slice(-4);
    const maskedPart = "X".repeat(accountNumber.length - 4);
    return maskedPart + lastFour;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-4">
        <Loader2 className="h-5 w-5 animate-spin text-primary mr-2" />
        <span>Loading bank accounts...</span>
      </div>
    );
  }

  if (banks.length === 0) {
    return (
      <div className="text-center p-4 border border-dashed rounded-md">
        <p className="text-gray-500">No bank accounts found</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {banks.map((bank) => (
        <div key={bank.id} className="border border-gray-200 rounded-md p-3">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard className="h-4 w-4 text-gray-500" />
            <span className="font-medium">{bank.bankName}</span>
            <Badge className={getAccountTypeColor(bank.accountType)}>
              {bank.accountType}
            </Badge>
          </div>
          <div className="text-sm">
            <div className="grid grid-cols-2 gap-2">
              <p className="text-gray-600">Account Number:</p>
              <p className="font-medium">{maskAccountNumber(bank.accountNumber)}</p>
              
              <p className="text-gray-600">IFSC Code:</p>
              <p>{bank.ifsc}</p>
              
              <p className="text-gray-600">Branch:</p>
              <p>{bank.branchName}</p>
              
              {bank.customerId && (
                <>
                  <p className="text-gray-600">Customer ID:</p>
                  <p>{bank.customerId}</p>
                </>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
